import {
	getContent,
	getServer,
	FileSystemItemModel,
	WebDavServerModel
} from "client/Api/Documents/WebDav/webDav";
import {
	BusyIndicator,
	ChangeTriggerKind
} from "client/Components";
import { contains, normalizeForSearch } from "client/Data/searching";
import { DestinationWindow } from "client/Api/Apps/common";
import {
	getActionsForLiveTile,
	OpenActionHandlers,
	WebDavDocumentAction,
	WebDavDocumentActionContext,
	getOpenActions
} from "client/Features/Documents/WebDav/actions";
import { generateTrackingId } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { WebDavSpecificFolder } from "client/Api/Workspace/LiveInformation/Documents/documentsLiveInformation";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent, TileScrollableHandle } from "../LiveTileContent";
import { WebDavConfiguration, documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getWebDavFolderUrl } from "client/Features/Documents/routing";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { DocumentListItem } from "./DocumentListItem";
import { DimensionToRender } from "../../RenderState/common";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice
} from "client/Hooks";

export type WebDavFolderTabProps = {
	appIcon: AppImageDisplayInfo;
	tabSelector: ReactElement;
	name: string;
	destinationWindow: DestinationWindow;
	folder: WebDavSpecificFolder;
	addAnalyticsEvent: () => void;
	dimension: DimensionToRender;
};

export const WebDavFolderTab = (props: WebDavFolderTabProps): ReactElement => {
	const {
		appIcon,
		tabSelector,
		name,
		destinationWindow,
		folder,
		dimension,
		addAnalyticsEvent
	} = props;

	const webDavConfiguration = useMemo(
		(): WebDavConfiguration | null => {
			const {
				webDav
			} = documentsConfiguration();

			return webDav;
		},
		[]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			const url: string = getWebDavFolderUrl(folder.serverId, folder.id);
			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[
			destinationWindow,
			folder.id,
			folder.serverId,
			addAnalyticsEvent
		]
	);

	if (!webDavConfiguration) {
		return (
			<LiveTileContent.Content>
				<LiveTileContent.Message text={localize("_YouDontHaveTheRequiredPackageToViewThisFolder")} />
			</LiveTileContent.Content>
		);
	}

	const shouldShowQuickActions: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	return (
		<>
			<LiveTileContent.Header icon={appIcon}>
				{tabSelector}
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<WebDavFolderContent
					serverId={folder.serverId}
					folder={folder}
					onAddAnalyticsEvent={addAnalyticsEvent}
					destinationWindow={destinationWindow}
					webDavConfiguration={webDavConfiguration}
					shouldShowQuickActions={shouldShowQuickActions}
				/>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</>
	);
};
WebDavFolderTab.displayName = "WebDavFolderTab";

const PAGE_SIZE = 10;

type LoadingState =
	{
		isLoading: true;
	} |
	{
		isLoading: false;
	}
	& (
		{
			hasError: true;
		}
		|
		{
			hasError?: undefined;
			server: WebDavServerModel;
			items: readonly FileSystemItemModel[];
		}
	);

type WebDavFolderContentProps = {
	serverId: number;
	folder: WebDavSpecificFolder;
	onAddAnalyticsEvent: () => void;
	destinationWindow: DestinationWindow;
	webDavConfiguration: WebDavConfiguration;
	shouldShowQuickActions: boolean;
};

const WebDavFolderContent = (props: WebDavFolderContentProps): ReactElement => {
	const {
		serverId,
		folder,
		onAddAnalyticsEvent,
		destinationWindow,
		webDavConfiguration,
		shouldShowQuickActions
	} = props;

	const [pageNumber, setPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const isTouch: boolean = useIsTouchDevice();
	const scrollableRef: RefObject<TileScrollableHandle | null> = useRef<TileScrollableHandle>(null);

	const [itemsToRender, hasMore] = useMemo(
		(): [items: readonly FileSystemItemModel[], hasMore: boolean] => {
			if (loadingState.isLoading || loadingState.hasError) {
				return [[], false];
			}

			const { items } = loadingState;
			const maxAmountToRender: number = (pageNumber + 1) * PAGE_SIZE;

			const normalizedSearchTerm: string = normalizeForSearch(searchTerm.value);

			const matchingItems: readonly FileSystemItemModel[] = items
				.filter((x) => contains(x.name, normalizedSearchTerm));

			const itemsToRender: readonly FileSystemItemModel[] = matchingItems
				.slice(0, maxAmountToRender);

			const hasMore: boolean = itemsToRender.length < matchingItems.length;

			return [itemsToRender, hasMore];
		},
		[
			loadingState,
			pageNumber,
			searchTerm.value
		]
	);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setLoadingState({
					isLoading: true
				});

				try {
					const server: WebDavServerModel = await getServer(serverId, signal);
					const items: FileSystemItemModel[] = await getContent(
						serverId,
						folder.id,
						signal
					);

					items.sort((a, b) => {
						if (a.isFolder !== b.isFolder) {
							if (a.isFolder) {
								return -1;
							} else {
								return 1;
							}
						}

						const aNormalized: string = (a.name || "").toLowerCase();
						const bNormalized: string = (b.name || "").toLowerCase();
						if (aNormalized > bNormalized) {
							return 1;
						} else if (aNormalized < bNormalized) {
							return -1;
						} else {
							return 0;
						}
					});

					setLoadingState({
						isLoading: false,
						server,
						items
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[folder.id, serverId]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setPageNumber(0);

			scrollableRef?.current
				?.scrollTo(0);
		},
		[searchTerm]
	);

	const searchTermChangeHandler = useCallback(
		(value: string, version: Guid): void => {
			setSearchTerm({
				value,
				version
			});
		},
		[]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			setPageNumber((x) => x + 1);
		},
		[]
	);

	const renderContent = (): ReactElement | null => {
		if (loadingState.isLoading) {
			return null;
		}

		if (loadingState.hasError) {
			return <LiveTileContent.Message text={localize("_WebDavThisFolderCanNotBeOpened")} />;
		}

		if (itemsToRender.length === 0) {
			return (
				<LiveTileContent.Message
					text={localize(
						loadingState.items.length === 0
							? "_NoItemsToShow"
							: "_NoResultsFound"
					)}
				/>
			);
		}

		return (
			<>
				<LiveTileContent.List trackingId="webdav-items">
					{itemsToRender.map((x): ReactElement => (
						<WebDavListItem
							key={x.id}
							trackingId={generateTrackingId(`|${x.id}`)}
							folder={folder}
							item={x}
							server={loadingState.server}
							destinationWindow={destinationWindow}
							webDavConfiguration={webDavConfiguration}
							onAddAnalyticsEvent={onAddAnalyticsEvent}
							shouldShowQuickActions={shouldShowQuickActions}
							isTouch={isTouch}
						/>
					))}
				</LiveTileContent.List>

				{hasMore && (
					<LiveTileContent.LoadMoreButton
						onClick={loadMoreHandler}
					/>
				)}
			</>
		);
	};

	return (
		<LiveTileContent.ContentStack>
			{(loadingState.isLoading || !loadingState.hasError) && (
				<LiveTileContent.SearchInput
					placeholder={localize("_Search")}
					value={searchTerm}
					trigger={{ kind: ChangeTriggerKind.Debounced }}
					onChange={searchTermChangeHandler}
				/>
			)}

			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}

				<LiveTileContent.Scrollable ref={scrollableRef}>
					{renderContent()}
				</LiveTileContent.Scrollable>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
WebDavFolderContent.displayName = "WebDavFolderContent";

type WebDavListItemProps = {
	item: FileSystemItemModel;
	server: WebDavServerModel;
	folder: WebDavSpecificFolder;
	trackingId: string;
	onAddAnalyticsEvent: () => void;
	destinationWindow: DestinationWindow;
	webDavConfiguration: WebDavConfiguration;
	shouldShowQuickActions: boolean;
	isTouch: boolean;
};

const WebDavListItem = (props: WebDavListItemProps): ReactElement => {
	const {
		item,
		server,
		folder,
		trackingId,
		onAddAnalyticsEvent,
		destinationWindow,
		webDavConfiguration,
		shouldShowQuickActions,
		isTouch
	} = props;

	const alertManager: AlertManager = useAlerts();

	const actionContext = useMemo(
		(): WebDavDocumentActionContext => ({
			selectedDocuments: [item],
			folder,
			server: server,
			// Defaults which we don't care for in the live tile
			isInSelectMode: false,
			isMobile: false
		}),
		[
			folder,
			item,
			server
		]
	);

	const handlers = useMemo(
		(): OpenActionHandlers=> {
			return {
				openFolder: (folder) => {
					const url: string = getWebDavFolderUrl(server.id, folder.id);
					navigateTo(url, destinationWindow === DestinationWindow.NewWindow);
				},
				selectItem: () => {
					// Do nothing at all
				}
			};
		},
		[
			destinationWindow,
			server.id
		]
	);

	const actions: readonly WebDavDocumentAction[] = useMemo(
		() => {
			return getActionsForLiveTile(
				actionContext,
				handlers,
				webDavConfiguration,
				alertManager
			).map(
				(action: WebDavDocumentAction): WebDavDocumentAction => ({
					...action,
					execute: (context: WebDavDocumentActionContext): void => {
						onAddAnalyticsEvent();
						action.execute(context);
					}
				})
			);
		},
		[
			actionContext,
			alertManager,
			handlers,
			onAddAnalyticsEvent,
			webDavConfiguration
		]
	);

	const openItemHandler = useCallback(
		(): void => {
			onAddAnalyticsEvent();

			const openActions: readonly WebDavDocumentAction[] = getOpenActions(
				handlers,
				actionContext.selectedDocuments,
				webDavConfiguration,
				alertManager
			);

			reduceToAvailableActions(openActions, actionContext)[0]
				?.execute(actionContext);
		},
		[
			onAddAnalyticsEvent,
			actionContext,
			alertManager,
			handlers,
			webDavConfiguration
		]
	);

	return (
		<DocumentListItem
			documentKind={item.isFolder ? DocumentItemType.Folder : DocumentItemType.File}
			trackingId={trackingId}
			extension={getExtensionAndType(item.name).extension}
			name={item.name}
			actionContext={actionContext}
			onClick={openItemHandler}
			actions={actions}
			isTouch={isTouch}
			shouldShowQuickActions={shouldShowQuickActions}
		/>
	);
};
WebDavListItem.displayName = "WebDavListItem";
